import { CartUpdateEvent } from '@theme/events';

// The switches reflect actual cart lines. No paid add-on is inserted without a customer action.
let activeRequest = false;

class ToeFixCartAddons extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
  }

  handleChange = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-toefix-addon-variant]')) return;
    const shouldAdd = input.checked;

    if (activeRequest) {
      input.checked = !shouldAdd;
      return;
    }

    const variantId = Number(input.dataset.toefixAddonVariant);
    const lineKey = input.dataset.toefixAddonKey;
    if (!Number.isSafeInteger(variantId) || (variantId <= 0) || (!shouldAdd && !lineKey)) {
      input.checked = !shouldAdd;
      this.showError('This option is unavailable. Please refresh your cart.');
      return;
    }

    activeRequest = true;
    this.setBusy(true);
    this.showError('');

    const root = window.Shopify?.routes?.root || '/';
    const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const sectionsToUpdate = [...new Set(sections)];
    const sectionInfo = {
      sections: sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    };

    let mutationCompleted = false;
    try {
      const endpoint = shouldAdd ? 'cart/add.js' : 'cart/change.js';
      const action = shouldAdd
        ? { items: [{ id: variantId, quantity: 1 }], ...sectionInfo }
        : { id: lineKey, quantity: 0, ...sectionInfo };

      const response = await fetch(`${root}${endpoint}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(action),
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) {
        throw new Error(result.description || result.message || 'Could not update your cart.');
      }
      mutationCompleted = true;

      // This must be fetched AFTER the mutation: cart/add.js returns added items,
      // not the cart total count, which cart-icon.js requires.
      const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
      if (!cartResponse.ok) throw new Error('Cart updated, but could not refresh. Please reload the page.');
      const cart = await cartResponse.json();

      // The existing Horizon section morph refreshes lines, totals, switches and cart bubble.
      document.dispatchEvent(new CartUpdateEvent(cart, 'toefix-cart-addons', {
        source: 'toefix-cart-addons',
        itemCount: cart.item_count,
        sections: result.sections || {},
      }));
    } catch (error) {
      // If the cart mutation succeeded but the refresh failed, reload to avoid an
      // inaccurate toggle and checkout total. Otherwise roll back the switch.
      if (mutationCompleted) {
        window.location.reload();
        return;
      }
      input.checked = !shouldAdd;
      this.showError(error instanceof Error ? error.message : 'Could not update your cart. Please try again.');
    } finally {
      activeRequest = false;
      // The section may have been replaced by the successful update.
      for (const group of document.querySelectorAll('toefix-cart-addons')) {
        if (group instanceof ToeFixCartAddons) group.setBusy(false);
      }
      this.setBusy(false);
    }
  };

  setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
    this.querySelectorAll('input[data-toefix-addon-variant]').forEach((input) => { input.disabled = busy; });
    const dialog = this.closest('.cart-drawer__dialog') || document.querySelector('.cart-drawer__dialog');
    dialog?.querySelectorAll('.cart__checkout-button').forEach((button) => {
      if (busy) {
        if (!button.hasAttribute('data-toefix-original-disabled')) {
          button.setAttribute('data-toefix-original-disabled', button.disabled ? 'true' : 'false');
        }
        button.disabled = true;
      } else if (button.hasAttribute('data-toefix-original-disabled')) {
        button.disabled = button.getAttribute('data-toefix-original-disabled') === 'true';
        button.removeAttribute('data-toefix-original-disabled');
      }
    });
  }

  showError(message) {
    const box = this.querySelector('.toefix-cart-addons__error');
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
  }
}

if (!customElements.get('toefix-cart-addons')) {
  customElements.define('toefix-cart-addons', ToeFixCartAddons);
}
